@if (failed) {
  <cs-info-card type="warning" [dismissible]="false">
    {{ LL.trainingOverview.loadFailed() }}
    <cs-button tertiary (click)="retry.emit()">{{ LL.trainingOverview.retry() }}</cs-button>
  </cs-info-card>
} @else if (!model) {
  <!-- no model yet: a spinner only while a load is really running -->
  @if (busy) {
    <div class="overview-loading">
      <cs-spinner></cs-spinner>
    </div>
  }
} @else {
  <div class="overview" [class.reloading]="busy">

    <!-- the host's own notes (for example "older workouts are still downloading") -->
    <ng-content></ng-content>

    @if (hasNoSessions) {
      <div class="empty-text no-workouts">{{ LL.trainingOverview.noWorkouts({ weeks: loadedWeeks }) }}</div>
    } @else {
      <!-- one card per workout; the host gives the side spacing, so this list adds none (#6676) -->
      <cs-card-list class="overview-cards">
        @for (section of model.sections; track $index; let sectionIndex = $index) {
          <cs-card class="overview-section" [attr.data-plan-reference]="section.planReference ?? 'other'">
            <cs-card-title>
              {{ section.title }}
              @if (canShowEarlier[sectionIndex] || canShowLater[sectionIndex]) {
                <div class="paging-buttons wide-only" title-action>
                  @if (canShowLater[sectionIndex]) {
                    <cs-button small secondary class="show-later" [label]="LL.trainingOverview.showLater()"
                      (click)="showLater.emit(sectionIndex)"></cs-button>
                  }
                  @if (canShowEarlier[sectionIndex]) {
                    <cs-button small secondary class="show-earlier" [label]="LL.trainingOverview.showEarlier()"
                      (click)="showEarlier.emit(sectionIndex)"></cs-button>
                  }
                </div>
              }
              @if (section.columns.length > 0) {
                <cs-note class="wide-only">{{ LL.trainingOverview.showingSessions({ shown: section.columns.length, total: section.sessionCount }) }}</cs-note>
                <cs-note class="phone-only">{{ phoneNote(section) }}</cs-note>
              }
            </cs-card-title>
            <cs-card-content>
              @if (section.sessionCount === 0) {
                <div class="empty-text">{{ LL.trainingOverview.noSessions() }}</div>
              } @else {

                <!-- wide screens: one table, oldest session left, newest right. Both layouts are drawn and
                     the app's small-screen class shows one, so a window resized across the phone width switches -->
                <cs-table class="overview-table wide-only" [style.--table-columns]="desktopColumns(section)">
                  <cs-table-row header>
                    <cs-table-header-cell>{{ LL.trainingOverview.columnExercise() }}</cs-table-header-cell>
                    <cs-table-header-cell>{{ LL.trainingOverview.columnSets() }}</cs-table-header-cell>
                    @for (column of section.columns; track column.workoutId) {
                      <cs-table-header-cell class="col-session" [attr.data-date]="column.dateIso" [attr.data-workout-id]="column.workoutId">
                        {{ headerDate(column.dateIso) }}
                      </cs-table-header-cell>
                    }
                  </cs-table-row>
                  @for (row of section.programRows; track row.exerciseId) {
                    <ng-container *ngTemplateOutlet="tableRow; context: { row: row, section: section }"></ng-container>
                  }
                  @if (section.greyRows.length > 0) {
                    @if (section.programRows.length > 0) {
                      <cs-table-row class="sub-heading">
                        <cs-table-cell class="sub-heading-cell">{{ LL.trainingOverview.noLongerInWorkout() }}</cs-table-cell>
                      </cs-table-row>
                    }
                    @for (row of section.greyRows; track row.exerciseId) {
                      <ng-container *ngTemplateOutlet="tableRow; context: { row: row, section: section }"></ng-container>
                    }
                  }
                </cs-table>

                <!-- phones: one row per exercise with its newest session; a tap opens the exercise menu -->
                <cs-table class="overview-table phone-table phone-only">
                  <cs-table-row header>
                    <cs-table-header-cell>{{ LL.trainingOverview.columnExercise() }}</cs-table-header-cell>
                    <cs-table-header-cell>{{ LL.trainingOverview.columnLastSession() }}</cs-table-header-cell>
                    <cs-table-header-cell></cs-table-header-cell>
                  </cs-table-row>
                  @for (row of section.programRows; track row.exerciseId) {
                    <ng-container *ngTemplateOutlet="phoneRow; context: { row: row }"></ng-container>
                  }
                  @if (section.greyRows.length > 0) {
                    @if (section.programRows.length > 0) {
                      <cs-table-row class="sub-heading">
                        <cs-table-cell class="sub-heading-cell">{{ LL.trainingOverview.noLongerInWorkout() }}</cs-table-cell>
                      </cs-table-row>
                    }
                    @for (row of section.greyRows; track row.exerciseId) {
                      <ng-container *ngTemplateOutlet="phoneRow; context: { row: row }"></ng-container>
                    }
                  }
                </cs-table>
              }
            </cs-card-content>
          </cs-card>
        }
      </cs-card-list>
    }
  </div>
}

<!-- one exercise's row in the wide table -->
<ng-template #tableRow let-row="row" let-section="section">
  <cs-table-row [class.grey-row]="!row.isInProgram" [attr.data-exercise-id]="row.exerciseId">
    <cs-table-cell class="col-exercise">
      @if (isTappable(row)) {
        <button type="button" class="row-button" (click)="onRowTap(row, $event)">{{ row.name }}</button>
      } @else {
        <span class="row-name">{{ row.name }}</span>
      }
    </cs-table-cell>
    <cs-table-cell class="col-sets">{{ setsTimesTarget(row) }}</cs-table-cell>
    @for (cell of row.cells; track $index) {
      <cs-table-cell class="col-session"
        [class.mark-same]="cell.mark === OverviewMark.Same"
        [class.mark-down]="cell.mark === OverviewMark.Down"
        [attr.data-date]="section.columns[$index]?.dateIso" [attr.data-workout-id]="section.columns[$index]?.workoutId">
        <ng-container *ngTemplateOutlet="cellContent; context: { cell: cell, reps: cell.repsText }"></ng-container>
      </cs-table-cell>
    }
  </cs-table-row>
</ng-template>

<!-- one exercise's row on a phone; cs-table-row reads "clickable" once, as a plain attribute, so a
     tappable row and a row that cannot be tapped are two branches -->
<ng-template #phoneRow let-row="row">
  @if (isTappable(row)) {
    <cs-table-row clickable [class.grey-row]="!row.isInProgram" [attr.data-exercise-id]="row.exerciseId" (rowClick)="onRowTap(row, $event)">
      <ng-container *ngTemplateOutlet="phoneCells; context: { row: row }"></ng-container>
    </cs-table-row>
  } @else {
    <cs-table-row [class.grey-row]="!row.isInProgram" [attr.data-exercise-id]="row.exerciseId">
      <ng-container *ngTemplateOutlet="phoneCells; context: { row: row }"></ng-container>
    </cs-table-row>
  }
</ng-template>

<ng-template #phoneCells let-row="row">
  <cs-table-cell class="col-exercise">
    <span class="row-name">{{ row.name }}</span>
    @if (row.isInProgram && setsTimesTarget(row) !== noValue) {
      <span class="row-targets">{{ setsTimesTarget(row) }}</span>
    }
  </cs-table-cell>
  <cs-table-cell class="col-session col-last-session"
    [class.mark-same]="row.lastSession?.cell.mark === OverviewMark.Same"
    [class.mark-down]="row.lastSession?.cell.mark === OverviewMark.Down"
    [attr.data-date]="row.lastSession?.dateIso">
    @if (row.lastSession) {
      <ng-container *ngTemplateOutlet="cellContent; context: { cell: row.lastSession.cell, reps: phoneReps(row.lastSession.cell) }"></ng-container>
    } @else {
      <span class="cell-reps">{{ noValue }}</span>
    }
  </cs-table-cell>
  <cs-table-cell class="col-chevron">
    @if (isTappable(row)) {
      <cs-icon name="arrow-dropright"></cs-icon>
    }
  </cs-table-cell>
</ng-template>

<!-- what one session holds for one exercise: the weight, the reps, then its badges and its chip -->
<ng-template #cellContent let-cell="cell" let-reps="reps">
  @if (cell.kind === OverviewCellKind.Skipped) {
    <span class="skipped">{{ LL.trainingOverview.skipped() }}</span>
  } @else if (cell.kind === OverviewCellKind.Trained) {
    <span class="cell-weight">{{ cell.weightText }}</span>
    <span class="cell-reps">{{ reps }}</span>
    @if (intensityText(cell) || sessionTagText(cell) || markText(cell)) {
      <span class="cell-chips">
        @if (intensityText(cell)) {
          <cs-badge class="tag" [color]="BadgeColor.Gray" [label]="intensityText(cell)" [info]="intensityInfo(cell)"></cs-badge>
        }
        @if (sessionTagText(cell)) {
          <cs-badge class="tag" [color]="BadgeColor.Blue" [label]="sessionTagText(cell)" [info]="sessionTagInfo(cell)"></cs-badge>
        }
        @if (markText(cell)) {
          <cs-status-chip class="mark" [tone]="markTone(cell)" [label]="markText(cell)" [info]="markInfo(cell)"></cs-status-chip>
        }
      </span>
    }
  }
</ng-template>
